import * as React from 'react';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { CheckBox, CheckBoxState } from 'sos-common/js/components/FormControls/CheckBox';
import { doGet, doPost } from 'sos-eservice-common/js/services';
import { ResponseError } from 'sos-common/js/utils/ResponseError';
import { dialogueErrorBody } from 'sos-eservice-common/js/config/dialogueErrorBodies';
import { DialogueError } from 'sos-common/js/components/DialogueError/DialogueError';
import LoadingSpinner from 'sos-common/js/components/LoadingSpinner/LoadingSpinner';
import {
  IStepTwoSubmitButtonProps,
  IStepTwoSubmitButtonState,
  RadioField,
  Kort,
  Swish,
  SwishM,
} from './StepTwoSubmitButton.types';
import { EducationLocation } from '../../models/ansokan.model';
import './StepTwoSubmitButton.scss';
import { AttentionBox } from '..';
import { SwishPaymentDialog } from '../SwishPayment/SwishPaymentDialog';
import { ISwishResponse } from '../SwishPayment/swishresponse.types';

export class StepTwoSubmitButton extends React.Component<IStepTwoSubmitButtonProps, IStepTwoSubmitButtonState> {
  private _mainRadioGroup: Map<string, RadioField>;
  private _subRadioGroup: RadioField[];
  private _paymentOptionsRef: React.RefObject<HTMLDivElement>;
  private _validationErrorMessage: string;
  static defaultProps = {
    masterCardText: 'Bankkort VISA eller Mastercard',
    errorHeader: 'Ett fel har inträffat',
    errorBody: 'Din ansökan har inte skickats in men finns sparad på Min sida. Var vänlig och försök senare.',
  };

  constructor(props: IStepTwoSubmitButtonProps) {
    super(props);
    this._paymentOptionsRef = React.createRef();
    this.state = {
      acceptConditionsChecked: false,
      isHealthcheckDialogOpen: false,
      isValidationErrorDialogOpen: false,
      isSwishPaymentDialogOpen: this.props.transactionId ? true : false,
      preSwishRedirect: false,
      showSpinner: false,
      completedSupplementaryEducation: this.props.completedSupplementaryEducation ?? false,
      transactionId: this.props.transactionId,
    };

    this._mainRadioGroup = new Map<string, RadioField>();

    this._mainRadioGroup.set(Swish, {
      label: (
        <>
          Swish <img src="/ClientSource/gfx/swish_logotype.svg" alt="Swish logga" />
        </>
      ),
      value: Swish,
      defaultChecked: true,
    });
    this._mainRadioGroup.set(Kort, {
      label: (
        <>
          {this.props.masterCardText}{' '}
          <img src="/ClientSource/gfx/visa_mastercard.png" alt="Visa/Mastercard logga" aria-hidden="true" />
        </>
      ),
      value: Kort,
      defaultChecked: false,
    });
  }

  onAcceptConditionsChecked = (state: CheckBoxState) => {
    this.setState({ acceptConditionsChecked: state.value });
  };

  renderRadioGroup = (
    fieldGroupName: string,
    fields: RadioField[],
    onRadioChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  ): JSX.Element[] => {
    return fields.map((field, index) => {
      return (
        <div key={index}>
          <label className="radio-checkbox-label" key={field.value}>
            <input
              type="radio"
              defaultChecked={field.defaultChecked}
              value={field.value}
              name={fieldGroupName}
              onChange={onRadioChange}
            />
            {field.label}
          </label>{' '}
          <br />
        </div>
      );
    });
  };

  renderBetalSattRadioButtonGroups = () => {
    const swishField = this._mainRadioGroup.get(Swish);
    const kortField = this._mainRadioGroup.get(Kort);

    const radioButtonGroupName = 'betalsatt';

    return (
      <div className="form-group-xs-small">
        <div>
          <label className="radio-checkbox-label" key={Swish}>
            <input type="radio" defaultChecked={true} value={Swish} name={radioButtonGroupName} />
            {swishField.label}
          </label>{' '}
          <br />
        </div>
        <div>
          <label className="radio-checkbox-label" key={Kort}>
            <input type="radio" defaultChecked={false} value={Kort} name={radioButtonGroupName} />
            {kortField.label}
          </label>{' '}
          <br />
        </div>
      </div>
    );
  };

  getDialogueBody = (redirectUrl: string): dialogueErrorBody => {
    return {
      body: (
        <React.Fragment>
          <div className="header">
            <h2>{this.props.errorHeader}</h2>
          </div>
          <p>{this.props.errorBody}</p>
        </React.Fragment>
      ),

      buttons: [
        {
          text: 'Till Min sida',
          cssClassNames: 'button button--white',
          redirectUrl: redirectUrl,
        },
        {
          text: 'Stäng',
          cssClassNames: 'button',
          redirectUrl: null,
        },
      ],
    };
  };

  getValidationDialogueBody = (redirectUrl: string): dialogueErrorBody => {
    return {
      body: (
        <React.Fragment>
          <div className="header">
            <h2>Valideringsfel</h2>
          </div>
          <p>{this._validationErrorMessage}</p>
        </React.Fragment>
      ),

      buttons: [
        {
          text: 'Tillbaka fill formuläret',
          cssClassNames: 'button button--white',
          redirectUrl: redirectUrl,
        },
        {
          text: 'Stäng',
          cssClassNames: 'button',
          redirectUrl: null,
        },
      ],
    };
  };

  onModalClose = () => {
    this.setState({
      isHealthcheckDialogOpen: false,
    });
  };

  onValidationModalClose = () => {
    this.setState({
      isValidationErrorDialogOpen: false,
    });
  };

  onSwishPaymentDialogClose = () => {
    this.setState({
      isSwishPaymentDialogOpen: false,
    });
  };

  onHealthCheckFail = () => {
    this.setState({ isHealthcheckDialogOpen: true, showSpinner: false });
  };

  onValidationFail = (msg: string) => {
    this._validationErrorMessage = msg;
    this.setState({ isValidationErrorDialogOpen: true, isHealthcheckDialogOpen: false, showSpinner: false });
  };

  onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const paymentOption = (
      this._paymentOptionsRef.current?.querySelector('input[name="betalsatt"]:checked') as HTMLInputElement
    )?.value;

    if (paymentOption === Swish) {
      this.setState({
        transactionId: undefined,
        isSwishPaymentDialogOpen: true,
        isHealthcheckDialogOpen: false,
        preSwishRedirect: true,
        showSpinner: false,
      });
      return;
    }

    this.setState({ showSpinner: true });
    //Checking extra validation and healtchcheck. Both need to be ok to submit the form.
    Promise.all([
      doGet<Boolean>('/_api/healthcheck'),
      doGet<Boolean>(`${this.props.validationBaseEndpoint}?Id=${this.props.ansokanId}`),
    ])
      .then((responses) => {
        const [healthCheckResponse, validationResponse] = responses;

        if (!healthCheckResponse || healthCheckResponse instanceof ResponseError) {
          this.setState({ isHealthcheckDialogOpen: true, showSpinner: false });
          return;
        }

        if (!validationResponse || validationResponse instanceof ResponseError) {
          this._validationErrorMessage = (validationResponse as ResponseError).body.errorMessage;
          this.setState({ isValidationErrorDialogOpen: true, isHealthcheckDialogOpen: false, showSpinner: false });
          return;
        }

        form.submit();
      })
      .catch(() => this.setState({ isHealthcheckDialogOpen: true, showSpinner: false }));
  };

  public render() {
    const {
      acceptConditionsChecked,
      isHealthcheckDialogOpen: isHealtcheckDialogOpen,
      isValidationErrorDialogOpen,
      isSwishPaymentDialogOpen,
      preSwishRedirect,
      transactionId,
      showSpinner,
    } = this.state;
    const {
      buttonText,
      label,
      conditionsText,
      isInEditMode,
      postLink,
      ansokanId,
      heading,
      myPageUrl,
      attentionBoxText,
      stepOneLink,
      pollLink,
      swishTranslations,
      swishPostLink,
      ansokanType,
      educationLocation,
      specialistBevisTyp,
      yrke,
      paidAssessmentFeeEarlier,
      completedSupplementaryEducation,
    } = this.props;

    return (
      <>
        {isHealtcheckDialogOpen && (
          <DialogueError
            dialogueErrorBody={this.getDialogueBody(myPageUrl)}
            isModalOpen={isHealtcheckDialogOpen}
            onModalClose={this.onModalClose}
            enableCloseIcon={false}
            shouldCloseOnOverlayClick={false}
          />
        )}

        {isValidationErrorDialogOpen && !isHealtcheckDialogOpen && (
          <DialogueError
            dialogueErrorBody={this.getValidationDialogueBody(stepOneLink)}
            isModalOpen={isValidationErrorDialogOpen}
            onModalClose={this.onValidationModalClose}
            enableCloseIcon={false}
            shouldCloseOnOverlayClick={false}
          />
        )}

        {isSwishPaymentDialogOpen && (
          <SwishPaymentDialog
            header={heading}
            translations={swishTranslations}
            validationPath={this.props.validationBaseEndpoint}
            ansokanId={ansokanId}
            ansokanType={ansokanType}
            swishPaymentLink={swishPostLink}
            transactionId={transactionId}
            pollLink={pollLink}
            preSwishRedirect={preSwishRedirect}
            educationLocation={this.props.educationLocation}
            specialistBevisTyp={specialistBevisTyp}
            completedSupplementaryEducation={completedSupplementaryEducation}
            paidAssessmentFeeEarlier={this.props.paidAssessmentFeeEarlier}
            onDialogClose={this.onSwishPaymentDialogClose}
            onHealthCheckFail={this.onHealthCheckFail}
            onValidationFail={this.onValidationFail}
          />
        )}

        <div className="payment-wrapper">
          <h2 id="payment-heading">{heading}</h2>
          {attentionBoxText && (
            <div className="m-b-2">
              <AttentionBox
                bodytext={attentionBoxText}
                cssClass="attentionbox--brown attentionbox--sharp"
                icon="Infocirkel"
              />
            </div>
          )}
          <form action={postLink} method="post" onSubmit={this.onFormSubmit}>
            <input type="hidden" name="ansokanId" value={ansokanId} />
            <input type="hidden" name="ansokanType" value={ansokanType} />
            <input type="hidden" name="eduLoc" value={educationLocation} />
            <input type="hidden" name="specialistbevisTyp" value={specialistBevisTyp} />
            {educationLocation === EducationLocation.EducatedOutsideEuEes && yrke === '100009' && (
              <input
                type="hidden"
                name="completedSupplementaryEducation"
                value={completedSupplementaryEducation ? 'true' : 'false'}
              />
            )}
            {educationLocation === EducationLocation.EducatedOutsideEuEes && (
              <input
                type="hidden"
                name="paidAssessmentFeeEarlier"
                value={paidAssessmentFeeEarlier ? 'true' : 'false'}
              />
            )}
            <div
              className="form-group-xs-small payment-options"
              ref={this._paymentOptionsRef}
              role="radiogroup"
              aria-labelledby="payment-heading">
              {(!paidAssessmentFeeEarlier || completedSupplementaryEducation) &&
                this.renderBetalSattRadioButtonGroups()}
            </div>
            <hr role="presentation" />
            <div dangerouslySetInnerHTML={setInnerHtml(conditionsText)}></div>
            <CheckBox
              stringValue="Jag bekräftar"
              onChangeCallback={this.onAcceptConditionsChecked}
              label={label}
              disabled={isInEditMode}
              fieldName="acceptConditionsStepTwo"
            />
            <div className="form-submit">
              {acceptConditionsChecked && (
                <button className="button relative" type="submit" disabled={showSpinner}>
                  {buttonText}
                  {showSpinner && <LoadingSpinner cssClasses="button-spinner" />}
                </button>
              )}
              {!acceptConditionsChecked && (
                <span className="button button--disabled" role="button" aria-disabled="true">
                  {buttonText}
                </span>
              )}
            </div>
          </form>
        </div>
      </>
    );
  }
}
